import apiService from './api';

/**
 * Normaliza qualquer formato de CEP para apenas 8 dígitos numéricos (sem traço, sem ponto).
 * Exemplo: "89041-400" -> "89041400"
 * Exemplo: "89.041-400" -> "89041400"
 *
 * @param {string|number} cep
 * @returns {string} 8 dígitos numéricos
 */
export function normalizarCep(cep) {
  return String(cep || '').replace(/\D/g, '').slice(0, 8);
}

/**
 * Formata o CEP para o padrão tradicional brasileiro com hífen (XXXXX-XXX).
 * Exemplo: "89041400" -> "89041-400"
 *
 * @param {string|number} cep
 * @returns {string} CEP formatado com traço
 */
export function formatarCep(cep) {
  const limpo = normalizarCep(cep);
  if (limpo.length !== 8) return limpo;
  return `${limpo.slice(0, 5)}-${limpo.slice(5)}`;
}

/**
 * Utilitário para fetch com timeout via AbortController
 */
async function fetchWithTimeout(url, options = {}, timeoutMs = 4000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * 1. Provedor Gratuito: ViaCEP
 * Endpoint aceita CEP sem traço: https://viacep.com.br/ws/{cep}/json/
 */
async function consultarViaCep(cleanCep) {
  try {
    const res = await fetchWithTimeout(`https://viacep.com.br/ws/${cleanCep}/json/`, {}, 4000);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || data.erro || (!data.localidade && !data.logradouro)) return null;

    return {
      cep: cleanCep,
      cepFormatado: formatarCep(cleanCep),
      logradouro: String(data.logradouro || '').trim(),
      bairro: String(data.bairro || '').trim(),
      cidade: String(data.localidade || '').trim(),
      uf: String(data.uf || '').trim().toUpperCase(),
      origem: 'viacep',
      raw: data,
    };
  } catch (err) {
    console.warn(`[cepService] Falha ViaCEP (${cleanCep}):`, err?.message || err);
    return null;
  }
}

/**
 * 2. Provedor Gratuito: BrasilAPI
 * Endpoint aceita CEP sem traço: https://brasilapi.com.br/api/cep/v1/{cep}
 */
async function consultarBrasilApi(cleanCep) {
  try {
    const res = await fetchWithTimeout(`https://brasilapi.com.br/api/cep/v1/${cleanCep}`, {}, 4000);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || (!data.city && !data.street)) return null;

    return {
      cep: cleanCep,
      cepFormatado: formatarCep(cleanCep),
      logradouro: String(data.street || '').trim(),
      bairro: String(data.neighborhood || '').trim(),
      cidade: String(data.city || '').trim(),
      uf: String(data.state || '').trim().toUpperCase(),
      origem: 'brasilapi',
      raw: data,
    };
  } catch (err) {
    console.warn(`[cepService] Falha BrasilAPI (${cleanCep}):`, err?.message || err);
    return null;
  }
}

/**
 * 3. Provedor Gratuito: OpenCEP
 * Endpoint aceita CEP sem traço: https://opencep.com/v1/{cep}
 */
async function consultarOpenCep(cleanCep) {
  try {
    const res = await fetchWithTimeout(`https://opencep.com/v1/${cleanCep}`, {}, 4000);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || data.error || (!data.localidade && !data.logradouro)) return null;

    return {
      cep: cleanCep,
      cepFormatado: formatarCep(cleanCep),
      logradouro: String(data.logradouro || '').trim(),
      bairro: String(data.bairro || '').trim(),
      cidade: String(data.localidade || '').trim(),
      uf: String(data.uf || '').trim().toUpperCase(),
      origem: 'opencep',
      raw: data,
    };
  } catch (err) {
    console.warn(`[cepService] Falha OpenCEP (${cleanCep}):`, err?.message || err);
    return null;
  }
}

/**
 * 4. Provedor Gratuito: ApiCEP
 * Endpoint EXIGE CEP COM TRAÇO: https://cdn.apicep.com/file/apicep/{cep-formatado}.json
 */
async function consultarApiCep(cleanCep) {
  try {
    const cepComTraco = formatarCep(cleanCep);
    const res = await fetchWithTimeout(`https://cdn.apicep.com/file/apicep/${cepComTraco}.json`, {}, 4000);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data || data.ok === false || (!data.city && !data.address)) return null;

    return {
      cep: cleanCep,
      cepFormatado: cepComTraco,
      logradouro: String(data.address || '').trim(),
      bairro: String(data.district || '').trim(),
      cidade: String(data.city || '').trim(),
      uf: String(data.state || '').trim().toUpperCase(),
      origem: 'apicep',
      raw: data,
    };
  } catch (err) {
    console.warn(`[cepService] Falha ApiCEP (${cleanCep}):`, err?.message || err);
    return null;
  }
}

/**
 * Busca dados de endereço pelo CEP.
 * Aceita o CEP com ou sem formatação (com traço, ponto ou somente números).
 *
 * 1. Normaliza o CEP de entrada removendo qualquer caractere não numérico.
 * 2. Tenta buscar no banco de dados local na tabela `siscep`.
 * 3. Se não existir no banco (ou faltar logradouro/cidade),
 *    percorre a lista de APIs públicas gratuitas:
 *    ViaCEP -> BrasilAPI -> OpenCEP -> ApiCEP (esta última com hífen obrigatório).
 *
 * @param {string|number} cep - CEP com ou sem formatação
 * @returns {Promise<{
 *   cep: string,
 *   cepFormatado: string,
 *   logradouro: string,
 *   bairro: string,
 *   cidade: string,
 *   uf: string,
 *   origem: 'siscep' | 'viacep' | 'brasilapi' | 'opencep' | 'apicep',
 *   raw?: any
 * } | null>}
 */
export async function buscarDadosCep(cep) {
  // Normaliza o CEP digitado (remove traço, pontos, espaços e limita a 8 dígitos)
  const cleanCep = normalizarCep(cep);
  if (cleanCep.length !== 8) {
    return null;
  }

  let dadosSiscep = null;

  // 1. Tentar buscar no banco de dados local na tabela siscep
  try {
    const res = await apiService.get(`/v1/siscep/cep/${cleanCep}`);
    if (res && (res.dslocal || res.dslogradouro || res.nrcep)) {
      dadosSiscep = {
        cep: cleanCep,
        cepFormatado: formatarCep(cleanCep),
        logradouro: String(res.dslogradouro || '').trim(),
        bairro: String(res.dsbairro || '').trim(),
        cidade: String(res.dslocal || '').trim(),
        uf: String(res.dsuf || '').trim().toUpperCase(),
        origem: 'siscep',
        raw: res,
      };

      // Se já temos cidade e logradouro completos no banco siscep, retorna diretamente
      if (dadosSiscep.cidade && dadosSiscep.logradouro) {
        return dadosSiscep;
      }
    }
  } catch (err) {
    console.warn(`[buscarDadosCep] Consulta na tabela siscep para CEP ${cleanCep} não retornou dados:`, err?.message || err);
  }

  // 2. Se não existir no banco de dados (ou faltar logradouro),
  // consultar em cascata as APIs gratuitas públicas
  const provedoresGratuitos = [
    consultarViaCep,
    consultarBrasilApi,
    consultarOpenCep,
    consultarApiCep,
  ];

  for (const consultarProvedor of provedoresGratuitos) {
    const resultado = await consultarProvedor(cleanCep);
    if (resultado && (resultado.cidade || resultado.logradouro)) {
      return {
        ...resultado,
        cep: cleanCep,
        cepFormatado: formatarCep(cleanCep),
        logradouro: resultado.logradouro || dadosSiscep?.logradouro || '',
        bairro: resultado.bairro || dadosSiscep?.bairro || '',
        cidade: resultado.cidade || dadosSiscep?.cidade || '',
        uf: resultado.uf || dadosSiscep?.uf || '',
      };
    }
  }

  // Se todos os serviços externos falharem mas tivermos cidade do banco siscep, retorna o siscep
  if (dadosSiscep && dadosSiscep.cidade) {
    return dadosSiscep;
  }

  return null;
}

export default buscarDadosCep;
